import api from './api';

export interface Audience {
  id: string;
  user_id: string;
  project_id?: string;
  name: string;
  description?: string;
  type: 'website_visitors' | 'customer_list' | 'leads' | 'converters' | 'cart_abandoners' | 'lookalike' | 'custom_rule';
  source: 'landing_page' | 'pixel_events' | 'csv_upload' | 'api_import' | 'crm_sync' | 'platform_sync';
  rules?: Record<string, any>;
  landing_page_ids?: string[];
  lookback_days: number;
  refresh_frequency: 'realtime' | 'hourly' | 'daily' | 'weekly' | 'manual';
  is_dynamic: boolean;
  last_refreshed_at?: string;
  size: number;
  matched_size: number;
  status: 'building' | 'ready' | 'syncing' | 'error' | 'archived';
  external_id_google?: string;
  external_id_meta?: string;
  external_id_linkedin?: string;
  sync_status?: Record<string, any>;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  project?: {
    id: string;
    name: string;
  };
}

export interface AudienceUser {
  id: string;
  audience_id: string;
  email?: string;
  phone?: string;
  visitor_id?: string;
  first_name?: string;
  last_name?: string;
  source: string;
  landing_page_id?: string;
  first_seen_at?: string;
  last_seen_at?: string;
  visit_count: number;
  page_views: number;
  converted: boolean;
  converted_at?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  is_active: boolean;
  created_at: string;
}

export interface CreateAudienceData {
  name: string;
  description?: string;
  type: Audience['type'];
  source?: Audience['source'];
  rules?: Record<string, any>;
  landing_page_ids?: string[];
  lookback_days?: number;
  refresh_frequency?: Audience['refresh_frequency'];
  is_dynamic?: boolean;
  pixel_event_types?: string[];
  project_id?: string;
}

export interface AudienceWithDetails extends Audience {
  user_count: number;
  recent_users: AudienceUser[];
  linked_campaigns: Array<{
    id: string;
    name: string;
    status: string;
    link_type: 'include' | 'exclude';
    bid_adjustment?: number;
  }>;
}

export interface LandingPageOption {
  id: string;
  name: string;
  slug: string;
  published_url: string;
  views: number;
  conversions: number;
  is_external?: boolean;
  external_url?: string;
}

export interface ExternalWebsite {
  id: string;
  name: string;
  slug: string;
  published_url: string;
  external_url: string;
  views: number;
  conversions: number;
  created_at: string;
  pixel_script_url: string;
  pixel_snippet: string;
}

// Get all audiences
export const getAudiences = async (params?: {
  project_id?: string;
  type?: string;
  status?: string;
  search?: string;
}) => {
  const response = await api.get('/audiences', { params });
  return response.data;
};

// Get single audience with details
export const getAudience = async (id: string): Promise<{ success: boolean; data: AudienceWithDetails }> => {
  const response = await api.get(`/audiences/${id}`);
  return response.data;
};

// Create audience
export const createAudience = async (data: CreateAudienceData) => {
  const response = await api.post('/audiences', data);
  return response.data;
};

// Update audience
export const updateAudience = async (id: string, data: Partial<CreateAudienceData>) => {
  const response = await api.put(`/audiences/${id}`, data);
  return response.data;
};

// Delete audience
export const deleteAudience = async (id: string) => {
  const response = await api.delete(`/audiences/${id}`);
  return response.data;
};

// Preview audience size
export const previewAudienceSize = async (data: {
  type: string;
  landing_page_ids?: string[];
  lookback_days?: number;
  rules?: Record<string, any>;
}) => {
  const response = await api.post('/audiences/preview-size', data);
  return response.data;
};

// Refresh audience
export const refreshAudience = async (id: string) => {
  const response = await api.post(`/audiences/${id}/refresh`);
  return response.data;
};

// Add users to audience (CSV import)
export const addUsersToAudience = async (id: string, users: Array<{
  email?: string;
  phone?: string;
  first_name?: string;
  last_name?: string;
  country?: string;
}>) => {
  const response = await api.post(`/audiences/${id}/users`, { users });
  return response.data;
};

// Get audience users
export const getAudienceUsers = async (id: string, params?: {
  page?: number;
  limit?: number;
  search?: string;
  converted?: boolean;
}) => {
  const response = await api.get(`/audiences/${id}/users`, { params });
  return response.data;
};

// Link audience to campaign
export const linkAudienceToCampaign = async (audienceId: string, data: {
  campaign_id: string;
  type?: 'include' | 'exclude';
  bid_adjustment?: number;
}) => {
  const response = await api.post(`/audiences/${audienceId}/campaigns`, data);
  return response.data;
};

// Unlink audience from campaign
export const unlinkAudienceFromCampaign = async (audienceId: string, campaignId: string) => {
  const response = await api.delete(`/audiences/${audienceId}/campaigns/${campaignId}`);
  return response.data;
};

// Sync audience to platform
export const syncAudienceToPlatform = async (audienceId: string, platform: 'google' | 'meta' | 'linkedin' | 'all') => {
  const response = await api.post(`/audiences/${audienceId}/sync/${platform}`);
  return response.data;
};

// Get available landing pages for audience (includes external websites)
export const getAvailableLandingPages = async (): Promise<{ success: boolean; data: LandingPageOption[] }> => {
  const response = await api.get('/audiences/landing-pages');
  return response.data;
};

// External websites management
export const getExternalWebsites = async (): Promise<{ success: boolean; data: ExternalWebsite[] }> => {
  const response = await api.get('/audiences/external-websites');
  return response.data;
};

export const createExternalWebsite = async (data: {
  name: string;
  url: string;
  project_id?: string;
}): Promise<{ success: boolean; data: ExternalWebsite; message: string }> => {
  const response = await api.post('/audiences/external-websites', data);
  return response.data;
};

export const deleteExternalWebsite = async (id: string) => {
  const response = await api.delete(`/audiences/external-websites/${id}`);
  return response.data;
};

export default {
  getAudiences,
  getAudience,
  createAudience,
  updateAudience,
  deleteAudience,
  previewAudienceSize,
  refreshAudience,
  addUsersToAudience,
  getAudienceUsers,
  linkAudienceToCampaign,
  unlinkAudienceFromCampaign,
  syncAudienceToPlatform,
  getAvailableLandingPages,
  getExternalWebsites,
  createExternalWebsite,
  deleteExternalWebsite
};
